import shuffle from "lodash/shuffle";
import values from "lodash/values";
import { ThunkResult } from "..";
import { ApiError, apiGet, apiPost } from "../../utils/api";
import { bootstrap } from "../bootstrap/thunks";
import { cancelProposedChip } from "../chips/actions";
import {
  getActiveChip,
  getActiveOrProposedChip,
  getActiveOrProposedTransferChipName,
  getProposedChip,
  getProposedChipName,
} from "../chips/reducers";
import { getElementTypesBySquadPosition } from "../element-types/reducers";
import {
  getAverageCostByType,
  getElement,
  getElements,
} from "../elements/reducers";
import { getNextEvent } from "../events/reducers";
import { getRules } from "../game/reducers";
import { getPlayerData } from "../player/reducers";
import {
  getCountByTeam,
  getProposedElements,
  getProposedTransfers,
  getSavedPicks,
  getToSpend,
  getTransferCostsWithoutChip,
  getTypesNeeded,
  toCreateEntryAPI,
} from "../squad/reducers";
import * as actions from "./actions";
import { resetProposed } from "./actions";
import {
  IAutopickPick,
  IAutopickPreferences,
  IAutopickResponse,
  ICreateEntryData,
} from "./types";

export const proposeElement =
  (elementId: number): ThunkResult<boolean> =>
  (dispatch, getState) => {
    const chip = getActiveOrProposedChip(getState());

    const element = getElement(getState(), elementId);
    const proposed = getProposedElements(getState(), undefined, chip?.id);
    const saved = getSavedPicks(getState());
    const elementTypesByPosition = getElementTypesBySquadPosition(
      getState(),
      undefined,
      chip?.id,
    );
    if (!element || !elementTypesByPosition) {
      return false;
    }

    // Element must not already be selected
    if (values(proposed).indexOf(element) > -1) {
      return false;
    }

    // Can we find a free position based on type?
    const freePosition = Object.keys(elementTypesByPosition).find((pos) => {
      return (
        elementTypesByPosition[pos].thisType.id === element.element_type &&
        !proposed[pos]
      );
    });
    if (!freePosition) {
      return false;
    }
    let position = Number(freePosition);

    // TODO - Look at bulk dispatching these with new react-redux
    const savedPositions = Object.keys(saved).filter(
      (p) => saved[p].element === elementId,
    );
    // We already have this element
    if (savedPositions.length) {
      const savedPosition = Number(savedPositions[0]);
      // In a different position
      if (savedPosition !== position) {
        const proposedId =
          proposed[savedPosition] && proposed[savedPosition].id;
        // Someone is in saved position so remove them and put in this position
        if (proposedId) {
          dispatch(actions.removeElementInPosition(proposedId, savedPosition));
          dispatch(actions.proposeElementInPosition(proposedId, position));
        }
        position = savedPosition;
      }
    }
    dispatch(actions.proposeElementInPosition(element.id, position));
    return true;
  };

export const removeElement =
  (position: number): ThunkResult<boolean> =>
  (dispatch, getState) => {
    const chip = getActiveOrProposedChip(getState());

    const proposed = getProposedElements(getState(), undefined, chip?.id);
    if (!proposed[position]) {
      return false;
    }
    dispatch(actions.removeElementInPosition(proposed[position].id, position));
    return true;
  };

export const restoreElement =
  (position: number): ThunkResult<boolean> =>
  (dispatch, getState) => {
    const saved = getSavedPicks(getState());
    if (!saved[position]) {
      return false;
    }
    dispatch(
      actions.proposeElementInPosition(saved[position].element, position),
    );
    // If proposing wildcard and no transfer costs cancel it
    if (
      getProposedChipName(getState()) === "wildcard" &&
      getTransferCostsWithoutChip(getState()) === 0
    ) {
      dispatch(cancelProposedChip("wildcard"));
    }
    return true;
  };

export const reset = (): ThunkResult<void> => (dispatch, getState) => {
  const savedPicks = getSavedPicks(getState());
  dispatch(
    resetProposed(
      Object.keys(savedPicks).reduce<Record<string, number>>(
        (memo, i) => ({ ...memo, [i]: savedPicks[i].element }),
        {},
      ),
    ),
  );
  // Cancel any proposed transfer chips
  const chip = getProposedChip(getState());
  if (chip && chip.chip_type === "transfer") {
    dispatch(cancelProposedChip(chip.name));
  }
};

export const autoComplete =
  (): ThunkResult<boolean> => (dispatch, getState) => {
    const chip = getActiveOrProposedChip(getState());

    const typesByPosition = getElementTypesBySquadPosition(
      getState(),
      undefined,
      chip?.id,
    );
    const rules = getRules(getState(), undefined, chip?.id);
    if (!rules || !typesByPosition) {
      return false;
    }
    const averageCostByType = getAverageCostByType(getState());
    const proposed = getProposedElements(getState(), undefined, chip?.id);
    const elementsOwned = values(proposed)
      .map((e) => e.id)
      .concat(values(getSavedPicks(getState())).map((p) => p.element));
    const countByTeam = getCountByTeam(getState(), undefined, chip?.id);
    let failures = 0;

    Object.keys(typesByPosition).forEach((pos) => {
      if (!proposed[pos]) {
        const typesNeeded = getTypesNeeded(getState(), undefined, chip?.id);
        const avgToSpend = Object.keys(typesNeeded).reduce(
          (memo, et) => memo + typesNeeded[et] * averageCostByType[et],
          0,
        );
        const weightingFactor =
          getToSpend(getState(), undefined, chip?.id) / avgToSpend;
        const invalidTeams = Object.keys(countByTeam)
          .filter((id) => countByTeam[id] >= rules.squad_team_limit)
          .map((id) => parseInt(id, 10));
        const possibles = getElements(getState()).filter(
          (e) =>
            // Correct type
            e.element_type === typesByPosition[pos].thisType.id &&
            // Cheap enough
            e.now_cost <=
              averageCostByType[typesByPosition[pos].thisType.id] *
                weightingFactor &&
            // Don't have
            elementsOwned.indexOf(e.id) === -1 &&
            // Available
            e.status === "a" &&
            // Within team limit
            invalidTeams.indexOf(e.team) === -1,
        );
        if (possibles.length) {
          const choice = shuffle(possibles).sort(
            (a, b) =>
              parseFloat(b.form) - parseFloat(a.form) ||
              b.now_cost - a.now_cost,
          )[0];
          dispatch(
            actions.proposeElementInPosition(choice.id, parseInt(pos, 10)),
          );
          elementsOwned.push(choice.id);
          countByTeam[choice.team]++;
        } else {
          failures++;
        }
      }
    });
    return !failures;
  };

// Previously this thunk included a call to navigate to "/my-team",
// this now needs to be done in the component where this thunk is called
export const createEntry =
  (data: ICreateEntryData): ThunkResult<Promise<boolean>> =>
  async (dispatch, getState) => {
    try {
      await apiPost(
        "entry-create/",
        { ...data, picks: toCreateEntryAPI(getState()) },
        dispatch,
        actions.createSquad,
      );
    } catch (e) {
      return false;
    }
    await dispatch(bootstrap());
    return true;
  };

// Previously this thunk included a call to navigate to "/my-team",
// this now needs to be done in the component where this thunk is called
export const makeTransfers =
  (): ThunkResult<Promise<boolean>> => async (dispatch, getState) => {
    const nextEvent = getNextEvent(getState());
    const activeChip = getActiveChip(getState());
    const player = getPlayerData(getState());

    if (!nextEvent || !player || !player.entry) {
      return false;
    }
    try {
      await apiPost(
        "transfers/",
        {
          chip: getActiveOrProposedTransferChipName(getState()),
          entry: player.entry,
          event: nextEvent.id,
          transfers: getProposedTransfers(
            getState(),
            undefined,
            activeChip?.id,
          ),
        },
        dispatch,
        actions.makeTransfers,
      );
    } catch (e) {
      return false;
    }
    return true;
  };

export const fetchAutopick =
  (
    preferences: IAutopickPreferences = {},
  ): ThunkResult<Promise<{ success: boolean; errorCode?: string }>> =>
  async (dispatch, getState) => {
    let picks: IAutopickPick[];
    try {
      const response = await apiPost<IAutopickResponse>(
        "entry-autopick/",
        preferences,
        dispatch,
        actions.fetchAutopick,
        undefined,
        // Handle autopick failures (e.g. 422 when no valid squad fits the
        // budget) locally instead of triggering the global server error page.
        { handleErrorsLocally: true },
      );
      picks = response.picks;
    } catch (e) {
      const apiError = e as ApiError;
      return {
        success: false,
        errorCode: apiError.badRequest?.non_field_error?.[0]?.code,
      };
    }
    if (!picks || !picks.length) {
      return { success: false };
    }

    // Propose every suggested pick that isn't already owned, in position order.
    // Already-owned picks are left exactly where they are. Returns false if any
    // new pick can't be proposed (e.g. element/rules not loaded, invalid,
    // duplicate) so callers can fall back to autoComplete() rather than leaving
    // a partially populated squad.
    const proposeNew = (ownedIds: Set<number>): boolean => {
      let allProposed = true;
      [...picks]
        .sort((a, b) => a.position - b.position)
        .forEach((pick) => {
          if (ownedIds.has(pick.element)) {
            return;
          }
          if (!dispatch(proposeElement(pick.element))) {
            allProposed = false;
          }
        });
      return allProposed;
    };

    // The endpoint returns a full 15 INCLUDING any players we asked it to build
    // around (preferences.existing_elements). When we sent some, keep those in
    // the positions the user already placed them and only drop the newly
    // suggested players into the empty slots. Otherwise (e.g. onboarding) there
    // is nothing to preserve, so replace the whole proposed squad.
    if (preferences.existing_elements?.length) {
      const ownedIds = new Set(
        values(getProposedElements(getState())).map((e) => e.id),
      );
      return { success: proposeNew(ownedIds) };
    }
    dispatch(resetProposed());
    return { success: proposeNew(new Set()) };
  };

export const fetchLatestTransfers =
  (): ThunkResult<void> => async (dispatch, getState) => {
    const player = getPlayerData(getState());
    if (!player || !player.entry) {
      return;
    }
    try {
      await apiGet(
        `entry/${player.entry}/transfers-latest/`,
        dispatch,
        actions.fetchTransfersLatest,
      );
    } catch (e) {
      return;
    }
  };
